import useApplicationManagerProject from 'Hooks/queries/useApplicationManagerProject';
import { useProject as useCreatorbaseProject } from '@round/creatorbase-helpers/api-hooks';
import { CREATORBASE_ROOT_PATH } from 'Modules/Navigation/whitelabel/routes';
import { buildAgencyProjectUrl } from 'Modules/Navigation/whitelabel/appRouting';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { useCurrentCreatorbaseUser } from 'Modules/Auth/hooks/useCurrentCreatorbaseUser';
import { useQuery } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';

export default function useCreatorbaseProjectNavigationLink(appManagerProjectId: number | null | undefined) {
    const isUserAdmin = useCheckUserGroupsAccess(['user_admin'], 'all');
    const { hasAccess: hasCreatorbaseAccess } = useCurrentCreatorbaseUser();
    const canNavigateToCreatorbaseProject = Boolean(hasCreatorbaseAccess && isUserAdmin);

    const { data: appManagerProject } = useApplicationManagerProject(appManagerProjectId, {
        enabled: canNavigateToCreatorbaseProject,
        refetchOnWindowFocus: false,
    });

    const creatorbaseProjectId = appManagerProject?.creatorbase_project_id;
    const hasCreatorbaseProjectId = typeof creatorbaseProjectId === 'number';
    // A mirrored project is owned by Round Agency: the client-surface endpoints hide it, so read
    // it and its team through the agency endpoints.
    const isMirroredToRoundAgency = !!appManagerProject?.is_mirrored_to_round_agency;

    const { data: legacyProject } = useCreatorbaseProject(creatorbaseProjectId, {
        enabled: hasCreatorbaseProjectId && !isMirroredToRoundAgency,
    });
    const { data: agencyProject } = useQuery({
        ...creatorbaseQueries.agency.project.options.detail(creatorbaseProjectId),
        enabled: hasCreatorbaseProjectId && isMirroredToRoundAgency,
    });
    const creatorbaseProject = isMirroredToRoundAgency ? agencyProject : legacyProject;

    const { data: legacyTeam } = useQuery({
        ...creatorbaseQueries.team.options.detail(creatorbaseProject?.team_id),
        enabled: !!creatorbaseProject?.team_id && !isMirroredToRoundAgency,
    });
    const { data: agencyTeam } = useQuery({
        ...creatorbaseQueries.agency.team.options.detail(creatorbaseProject?.team_id),
        enabled: !!creatorbaseProject?.team_id && isMirroredToRoundAgency,
    });

    const { data: brand } = useQuery({
        ...creatorbaseQueries.brand.options.detail(creatorbaseProject?.brand_id),
        enabled: !!creatorbaseProject?.brand_id,
    });

    const creatorbaseProjectUrl =
        creatorbaseProject && canNavigateToCreatorbaseProject
            ? isMirroredToRoundAgency
                ? buildAgencyProjectUrl(creatorbaseProject.id)
                : `/${CREATORBASE_ROOT_PATH}/projects/${creatorbaseProject.id}/`
            : null;

    return {
        creatorbaseProjectUrl,
        isMirroredToRoundAgency,
        creatorbaseProject,
        creatorbaseTeam: isMirroredToRoundAgency ? agencyTeam : legacyTeam,
        creatorbaseBrand: brand,
    };
}
